// Adding picked files to Documents (M28). A device still without its key
// can't send into an encrypted family; nothing is uploaded, and the files
// are kept under that family (not whichever is on screen) until Retry, by
// hand or when the family's keys arrive.
import { atom, getDefaultStore } from 'jotai';
import { ApiError, type FamilyDocument } from './api';
import { addDocumentFile } from './e2ee/apps';
import { KEYS_NOT_READY } from './e2ee/plainUpload';

export const heldDocumentsAtom = atom<Record<string, File[]>>({});

const store = getDefaultStore();

function hold(familyId: string, files: File[]) {
  store.set(heldDocumentsAtom, (held) => {
    const next = { ...held };
    if (files.length) next[familyId] = files;
    else delete next[familyId];
    return next;
  });
}

/** Cancel: forgets the files this family was waiting to send. */
export function dropHeldDocuments(familyId: string) {
  hold(familyId, []);
}

/**
 * Uploads each file into `familyId` in turn. 'held' when the device isn't
 * ready for an encrypted family (the rest are kept); other errors throw.
 */
export async function uploadDocumentFiles(
  familyId: string,
  files: File[],
  onAdded: (familyId: string, document: FamilyDocument) => void
): Promise<'done' | 'held'> {
  const queue = [...files];
  // Files already waiting stay waiting, beside any picked now.
  const earlier = (store.get(heldDocumentsAtom)[familyId] ?? []).filter(
    (file) => !files.includes(file)
  );
  hold(familyId, earlier);
  try {
    while (queue.length) {
      const response = await addDocumentFile(familyId, queue[0]!);
      queue.shift();
      onAdded(familyId, response.data);
    }
    return 'done';
  } catch (err) {
    if (err instanceof ApiError && err.code === KEYS_NOT_READY) {
      hold(familyId, [...earlier, ...queue]);
      return 'held';
    }
    throw err;
  }
}

/** Sends this family's held files again; 'none' when it has none. */
export async function retryHeldDocuments(
  familyId: string,
  onAdded: (familyId: string, document: FamilyDocument) => void
): Promise<'done' | 'held' | 'none'> {
  const files = store.get(heldDocumentsAtom)[familyId];
  if (!files?.length) return 'none';
  return uploadDocumentFiles(familyId, files, onAdded);
}
